import { PropertyValues, TemplateResult, html, nothing } from 'lit';
import { property, customElement, state, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { InputWidthValues } from '../../gzInput';
import { ButtonColor, ButtonFill, ButtonSize, ButtonType, FontSize } from '../../gzIconButton/types';
import { GzTextareaWebComponent } from '../../gzInput';
import { GzFormFieldWebComponent } from '../../gzForm/formField';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../gzButton/types';
import { debounce, isEmpty } from 'lodash';
import { FormFieldInput } from '../../gzForm/formField/component/FormFieldInput';
import { GzTextareaWithSuggestionsEvents, Suggestion } from '../types';
import { classMap } from 'lit/directives/class-map.js';
import { TooltipSeverity } from '../../gzTooltip/types';
import { GzButtonWebComponent } from '../../gzButton';
import { MaxLengthValidator } from '../../gzForm/validators/max-length.validator';

@customElement('gz-textarea-with-suggestions')
export class GzTextareaWithSuggestionsWebComponent extends FormFieldInput<string> {
  @property({ type: Boolean, attribute: true, reflect: true })
  disabled = false;

  @property({ type: String, attribute: true, reflect: true })
  placeholder = '';

  @property({ type: Boolean, attribute: true, reflect: true })
  hasErrorState = false;

  @property({ attribute: true, reflect: true })
  inputWidth: InputWidthValues;

  @property({ type: String, attribute: true, reflect: true })
  actionButtonIcon = 'ds-icon-send';

  @property({ type: String, attribute: true, reflect: true })
  defaultHintText: string;

  @property({ type: String, attribute: true, reflect: true })
  disabledTextareaHintText: string;

  @property({ type: String, attribute: true, reflect: true })
  suggestionActionButtonText: string;

  @property({ type: String, attribute: true, reflect: true })
  maxCharsErrorText = 'Max characters limit reached[***]';

  @property({ type: String, attribute: true, reflect: true })
  requiredErrorText = 'This field is required[***]';

  @property({ type: String, attribute: true, reflect: true })
  errorText: string;

  @property({ type: Number, attribute: true, reflect: true })
  maxChars: number;

  @property({ type: Array, attribute: true, reflect: true })
  defaultSuggestionList: Array<Suggestion>;

  @property({ type: Boolean, reflect: true }) isOpen = false;

  @property({ type: Boolean, attribute: true, reflect: true }) showSuggestionActionButton = true;

  @state() private disabledButtons = true;

  @state() private disableSendButton = true;

  @query('#textarea')
  private textarea: GzTextareaWebComponent;

  @query('#textarea-form-field')
  private textareaFormField: GzFormFieldWebComponent<GzTextareaWebComponent, string>;

  @query('.hidden-action-button')
  private hiddenActionButton: GzButtonWebComponent;

  @state()
  private defaultSuggestionListCopy: Array<Suggestion>;

  @state()
  private filteredSuggestions: Array<Suggestion>;

  @state()
  private suggestionOverlayTop: number;

  @state() textareaOffsetWidth: number;

  @state() suggestionActionButtonWidth = 0;

  private resizeObserver: ResizeObserver | null = null;

  public debounceExpandSuggestions = debounce(this.expandSuggestions, 100);

  private boundDocumentClickHandler = this.documentClickHandler.bind(this);

  // FROM UI/UX MOCK
  private readonly TEXTAREA_MARGIN_TOP = 6;
  private readonly TEXTAREA_MAX_HEIGHT = 290;
  private readonly SINGLE_OPTION_HEIGHT = 32;
  private readonly COUNTER_MAX_WIDTH = 65;
  private readonly SUGGESTION_PADDINGS = 35;

  public setFormValue(value: string): void {
    if (this.textareaFormField) {
      this.textareaFormField.value = value;
    }
  }

  public getFormValue(): string {
    return this.textareaFormField.value;
  }

  static get styles() {
    return styles;
  }

  protected firstUpdated(_changedProperties): void {
    if (this.defaultSuggestionList) {
      this.makeDefaultSuggestionListCopy();
      this.resetFilteredSuggestions();
      this.updateSuggestionOverlayTopValue();
    }

    if (this.hasErrorState) {
      this.textareaFormField?.setError('');
    }

    this.resizeObserver = new ResizeObserver(debounce(this.handleResize.bind(this), 300));
    if (this.textarea) this.resizeObserver.observe(this.textarea);

    this.textareaFormField.Validators = [MaxLengthValidator(this.maxChars, this.maxCharsErrorText)];
  }

  protected updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    if (changedProperties.has('filteredSuggestions')) {
      this.updateSuggestionOverlayTopValue();
    } else if (changedProperties.has('hasErrorState')) {
      if (this.hasErrorState) {
        this.textareaFormField?.setError('');
      } else {
        this.textareaFormField?.resetField();
      }
    } else if (changedProperties.has('defaultSuggestionList')) {
      this.makeDefaultSuggestionListCopy();
      this.resetFilteredSuggestions();
    }

    if (this.showSuggestionActionButton) {
      if (this.hiddenActionButton && this.suggestionActionButtonWidth === 0) {
        this.suggestionActionButtonWidth = this.hiddenActionButton.offsetWidth;

        if (this.suggestionActionButtonWidth > 0) {
          this.hiddenActionButton.style.display = 'none';
        }
      }
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('click', this.boundDocumentClickHandler);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this.boundDocumentClickHandler);

    if (this.textarea) this.resizeObserver?.unobserve(this.textarea);
  }

  private handleResize = (): void => {
    this.textareaOffsetWidth = this.textarea?.offsetWidth;
  };

  render(): TemplateResult {
    return html` <div class="main-wrapper max-width-900">
        <div class="d-flex max-width-900">${this.renderTextarea()} ${this.renderButtons()}</div>
        ${this.renderSuggestionsOverlay()}
      </div>
      ${this.renderHiddenButton()}`;
  }

  // this button is rendered to compute action button offsetWidth
  private renderHiddenButton(): TemplateResult | undefined {
    if (!this.showSuggestionActionButton) {
      return;
    }

    return html`<gz-button
      class="hidden-action-button"
      type="${ButtonTypes.QUIET}"
      buttonheight="${ButtonHeights.SMALL}"
      buttonfontsize="${ButtonHeights.SMALL}"
      lowercase
      >${this.suggestionActionButtonText}</gz-button
    >`;
  }

  private renderSuggestionsOverlay(): TemplateResult | undefined {
    if (!this.filteredSuggestions || this.filteredSuggestions?.length === 0) {
      return;
    }

    const style = `top: ${this.suggestionOverlayTop}px; width: ${this.textarea.offsetWidth}px`;

    return html`<div id="suggestion-overlay" style=${style}>
      <div id="list-wrapper">
        <ul id="list" class="dropdown-list--inner list-unstyled">
          ${this.renderOptionsList(this.filteredSuggestions)}
        </ul>
      </div>
    </div>`;
  }

  private renderOptionsList(options: Array<Suggestion>): TemplateResult {
    return html`${options.map((option, index) => this.renderListItem(option, index, this.textarea.value))}`;
  }

  private renderListItem(option: Suggestion, index: number, matchText?: string): TemplateResult {
    const classes = {
      disabled: (option.answered && this.showSuggestionActionButton) ?? false,
    };

    const displayedText = this.getSuggestionTextToDisplay(option, matchText);
    const tooltipStyle = this.getTooltipStyle();

    return html`
      <li
        id="${option.id ? option.id : index}"
        class="${classMap(classes)}"
        @click="${() => this.handleSuggestionSelection(option)}"
      >
        <div class="d-flex justify-space-between">
          <gz-tooltip tooltiptext="${option.text}" isellipsis="true" class="tooltip" style=${tooltipStyle}>
            <span>${displayedText}</span>
          </gz-tooltip>
          ${option.answered && this.showSuggestionActionButton
            ? html`<gz-button
                type="${ButtonTypes.QUIET}"
                buttonheight="${ButtonHeights.SMALL}"
                buttonfontsize="${ButtonHeights.SMALL}"
                @click="${event => this.handleViewAnswerButtonClick(event, option.text)}"
                lowercase
                >${this.suggestionActionButtonText}</gz-button
              >`
            : nothing}
        </div>
      </li>
    `;
  }

  private getTooltipStyle(): string {
    return this.checkIfHasShownActionButtons() && this.showSuggestionActionButton
      ? `max-width: ${this.textarea.offsetWidth - this.suggestionActionButtonWidth - this.SUGGESTION_PADDINGS}px`
      : ``;
  }

  private renderButtons(): TemplateResult {
    return html` <div class="d-flex buttons-wrapper align-items-center">
      ${this.renderSendButton()}${this.renderClearButton()}
    </div>`;
  }

  private renderTextarea(): TemplateResult {
    return html`
      <gz-form-field id="textarea-form-field" label="">
        <gz-textarea
          gz-form-input
          .placeholder="${this.placeholder}"
          id="textarea"
          showErrorIcon
          inputWidth="${this.inputWidth}"
          ?allowBeyondMaxChars="${true}"
          hasCounter
          maxChars="${this.maxChars}"
          @change="${this.handleTextareaChange}"
          @keydown="${this.handleTextareaKeydown}"
          ?disabled="${this.disabled}"
          @click="${this.debounceExpandSuggestions}"
          @blur="${this.handleBlurTextarea}"
        ></gz-textarea>
        ${this.renderHintText()}
      </gz-form-field>
    `;
  }

  private renderHintText(): TemplateResult {
    const hintText = this.getHintText();
    const hintTextStyle = `width: ${this.textareaOffsetWidth - this.COUNTER_MAX_WIDTH}px`;
    const classes = {
      'hint-text': true,
      'visible-hint': hintText?.length > 0,
    };

    return html`<gz-tooltip
      tooltiptext="${hintText}"
      isellipsis
      class="tooltip"
      style=${hintTextStyle}
      tooltipClass=${this.hasErrorState ? TooltipSeverity.ERROR : TooltipSeverity.NEUTRAL}
    >
      <span class="${classMap(classes)}">${hintText}</span>
    </gz-tooltip>`;
  }

  private renderSendButton(): TemplateResult {
    return html`<gz-icon-button
      id="send-button"
      class="ml-2"
      icon="${this.actionButtonIcon}"
      ?disabled="${this.disabledButtons || this.disabled || this.disableSendButton}"
      fontSize="${FontSize.ICON_SIZE_18}"
      size="${ButtonSize.SIZE_32}"
      buttonFill="${ButtonFill.PRIMARY}"
      buttonType="${ButtonType.SQUARE}"
      buttonColor="${ButtonColor.WHITE}"
      @click="${this.handleSendButtonClick}"
    ></gz-icon-button>`;
  }

  private renderClearButton(): TemplateResult {
    return html`<gz-button
      id="clear-button"
      class="ml-2 d-flex align-items-center"
      type="${ButtonTypes.QUIET}"
      color="${ButtonColors.SECONDARY}"
      buttonheight="${ButtonHeights.SMALL}"
      buttonfontsize="${ButtonFontSizes.SMALL}"
      ?disabled="${this.disabledButtons || this.disabled}"
      lowercase
      @click="${this.handleClearButtonClick}"
      >${translateFn('Clear')}</gz-button
    >`;
  }

  private getSuggestionTextToDisplay(option: Suggestion, matchText?: String): TemplateResult {
    let displayedText = html`${option.text}`;
    if (matchText) {
      const suggestionLowerCase = option.text.toLowerCase();
      const foundMatchIndex = suggestionLowerCase.indexOf(matchText.toLowerCase());

      displayedText = html`<span
        >${option.text.substring(0, foundMatchIndex)}<span class="bold"
          >${option.text.substring(foundMatchIndex, foundMatchIndex + matchText.length)}</span
        >${option.text.substring(foundMatchIndex + matchText.length)}</span
      >`;
    }

    return displayedText;
  }

  private getHintText(): String {
    if (this.disabled && this.disabledTextareaHintText) {
      return this.disabledTextareaHintText;
    } else if (!this.hasErrorState && this.defaultHintText) {
      return this.defaultHintText;
    } else if (this.hasErrorState) {
      return this.errorText;
    }

    return '';
  }

  private handleViewAnswerButtonClick(event: Event, option: string): void {
    event.stopPropagation();

    this.dispatchEvent(
      new CustomEvent(GzTextareaWithSuggestionsEvents.VIEW_ANSWER, {
        detail: {
          question: option,
        },
        bubbles: true,
        composed: true,
      })
    );

    this.textareaFormField.resetField();
    this.isOpen = false;
  }

  private handleSuggestionSelection(option: Suggestion): void {
    if (option) {
      this.setFormValue(option.text);
      this.isOpen = false;
    }

    this.dispatchEvent(
      new CustomEvent(GzTextareaWithSuggestionsEvents.SELECT_SUGGESTION_FROM_LIST, {
        detail: {
          id: option.id,
          suggestion: option.text,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleSendButtonClick(): void {
    const textareaValue = this.textarea.value;
    if (!textareaValue) return;
    this.disabledButtons = true;

    this.dispatchEvent(
      new CustomEvent(GzTextareaWithSuggestionsEvents.SEND_SUGGESTION, {
        detail: {
          suggestion: textareaValue,
        },
        bubbles: true,
        composed: true,
      })
    );

    this.setFormValue('');
    this.isOpen = false;

    if (this.showSuggestionActionButton) {
      this.checkIfDefaultSuggestionWasSend(textareaValue);
    }
  }

  private checkIfDefaultSuggestionWasSend(suggestion: string): void {
    // remove unused spaces around text
    const trimmedSuggestion = suggestion.trim();

    const index = this.defaultSuggestionListCopy.findIndex(
      item => item.text.toLowerCase() === trimmedSuggestion.toLowerCase()
    );

    if (index !== -1) {
      // a suggestion from default suggestion list was send
      this.defaultSuggestionListCopy[index].answered = true;
      this.resetFilteredSuggestions();

      this.dispatchEvent(
        new CustomEvent(GzTextareaWithSuggestionsEvents.DEFAULT_SUGGESTION_SELECTED, {
          detail: {
            id: this.defaultSuggestionListCopy[index].id,
            suggestion: this.defaultSuggestionListCopy[index].text,
          },
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  private resetFilteredSuggestions(): void {
    this.filteredSuggestions = JSON.parse(JSON.stringify(this.defaultSuggestionListCopy));
  }

  private makeDefaultSuggestionListCopy(): void {
    this.defaultSuggestionListCopy = JSON.parse(JSON.stringify(this.defaultSuggestionList));
  }

  private handleClearButtonClick(): void {
    if (!this.textarea?.value) return;

    this.textarea.value = '';
    this.disabledButtons = true;
    this.resetFilteredSuggestions();
  }

  private handleBlurTextarea(): void {
    setTimeout(() => {
      this.isOpen = false;
    }, 200);
  }

  private handleTextareaChange(): void {
    this.hasErrorState = false;
    const inputValue = this.textarea?.value?.toLowerCase();
    this.disabledButtons = isEmpty(inputValue);
    this.disableSendButton = this.disabledButtons || inputValue.length > this.maxChars;

    const filteredSuggestions = this.defaultSuggestionListCopy.filter(suggestion =>
      suggestion.text.toLowerCase().includes(inputValue)
    );

    if (filteredSuggestions?.length > 0 && !this.isOpen) {
      this.isOpen = true;
    }

    this.filteredSuggestions = JSON.parse(JSON.stringify(filteredSuggestions));
  }

  private handleTextareaKeydown(event): void {
    if (event.ctrlKey && event.key === '/') {
      this.resetFilteredSuggestions();
      this.debounceExpandSuggestions();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.handleSendButtonClick();
    }

    //prevent adding space at the beginning of the textarea
    if (this.textarea.value.length === 0 && event.key === ' ') {
      event.preventDefault();
      return;
    }
  }

  private expandSuggestions(): void {
    if (!this.hasErrorState) {
      this.isOpen = true;
    }
  }

  private documentClickHandler(): void {
    this.isOpen = false;
  }

  private updateSuggestionOverlayTopValue(): void {
    const listLength = this.filteredSuggestions?.length;

    if (listLength) {
      const overlayHeight = listLength > 8 ? this.TEXTAREA_MAX_HEIGHT : this.SINGLE_OPTION_HEIGHT * listLength;
      this.suggestionOverlayTop = -1 * (this.TEXTAREA_MARGIN_TOP + overlayHeight);
    }
  }

  private checkIfHasShownActionButtons(): boolean {
    return this.defaultSuggestionListCopy.findIndex(suggestion => suggestion.answered) !== -1;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-textarea-with-suggestions': GzTextareaWithSuggestionsWebComponent;
  }
}
